import axios from 'axios';

import {
  ProxyRegistrationRequest,
  ProxySigningRequest,
  ProxySigningResponse,
  ProxyWalletResponse,
  WalletLinkRequest,
  WalletResponse,
} from '@/types';
import {
  BridgeOutboundRoute,
  BridgeQuote,
  PrepareBridgeParams,
  PrepareResponse,
  StatusResponse,
} from '@/types/withdrawal.types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS, ONRAMPER_API_KEY, ONRAMPER_API_URL, RELAYER_URL } from '@/constants';

export interface SupportedAsset {
  id: string;
  code: string;
  name: string;
  type: string;
  decimals: number;
  network?: string;
  symbol?: string;
  logoUrl?: string;
}

export interface PaymentType {
  paymentTypeId: string;
  name: string;
  icon?: string;
  details?: any;
}

export interface ProviderQuote {
  ramp: string;
  payout: number;
  fees: number;
  totalFee: number;
  networkFee?: number;
  rate: number;
  icon?: string;
  logoUrl?: string;
  errors?: any[];
}

const onramperClient = axios.create({
  baseURL: ONRAMPER_API_URL,
  headers: {
    Authorization: ONRAMPER_API_KEY,
    'Content-Type': 'application/json',
  },
});

/**
 * Service object handling all wallet and transfer-related API communications.
 * Follows the Single Responsibility Principle by encapsulating all external
 * and internal HTTP requests for wallet linking, proxy management, and fiat transfers.
 */
export const walletApi = {
  /**
   * Links a user's wallet to their account.
   *
   * @param payload - The wallet linking request containing public address and signature.
   * @returns Information about the linked wallet.
   * @throws Will throw an error if the backend request fails.
   */
  linkWallet: async (payload: WalletLinkRequest): Promise<WalletResponse> => {
    const { data } = await axiosClient.post<WalletResponse>(API_ENDPOINTS.AUTH.WALLET, payload);
    return data;
  },

  /**
   * Retrieves an HMAC signature required for Polymarket remote signing operations.
   *
   * @param payload - The proxy signing request payload.
   * @returns The generated HMCA signature response.
   * @throws Will throw an error if the signing request fails.
   */
  signProxy: async (payload: ProxySigningRequest): Promise<ProxySigningResponse> => {
    const { data } = await axiosClient.post<ProxySigningResponse>(
      API_ENDPOINTS.AUTH.PROXY_WALLET_SIGN,
      payload,
    );
    return data;
  },

  /**
   * Registers the final proxy wallet (Gnosis Safe) address with the backend.
   * This is part of the legacy frontend deployment flow.
   *
   * @param payload - Application and wallet details to register.
   * @returns The registered proxy wallet response.
   */
  registerProxy: async (payload: ProxyRegistrationRequest): Promise<ProxyWalletResponse> => {
    const { data } = await axiosClient.post<ProxyWalletResponse>(
      API_ENDPOINTS.AUTH.PROXY_WALLET,
      payload,
    );
    return data;
  },

  /**
   * Retrieves the previously registered proxy wallet info for the currently authenticated user.
   *
   * @returns The proxy wallet information.
   */
  getProxyInfo: async (): Promise<ProxyWalletResponse> => {
    const { data } = await axiosClient.get<ProxyWalletResponse>(API_ENDPOINTS.AUTH.PROXY_WALLET);
    return data;
  },

  /**
   * Fetches supported fiat and crypto assets directly from the Onramper API.
   *
   * @returns An object containing filtered arrays of fiat and crypto assets.
   * @throws Will log and throw an error if the Onramper API is unreachable.
   */
  getSupportedAssets: async (): Promise<{
    fiat: SupportedAsset[];
    crypto: SupportedAsset[];
  }> => {
    try {
      const response = await onramperClient.get(API_ENDPOINTS.ONRAMPER.SUPPORTED);
      return {
        fiat: response.data.message.fiat || [],
        crypto: response.data.message.crypto || [],
      };
    } catch (error) {
      console.error('Failed to fetch supported assets from Onramper:', error);
      throw new Error('Could not retrieve supported assets at this time.');
    }
  },

  /**
   * Fetches supported countries from Onramper.
   */
  getSupportedCountries: async (): Promise<{ code: string; name: string }[]> => {
    try {
      const response = await onramperClient.get(API_ENDPOINTS.ONRAMPER.COUNTRIES);
      const countries = response.data.message || [];
      return countries.map((c: any) => ({
        code: c.countryCode,
        name: c.countryName,
      }));
    } catch (error) {
      console.error('Failed to fetch supported countries from Onramper:', error);
      return [];
    }
  },

  /**
   * Fetches defaults (auto-detected country based on IP)
   */
  getDefaults: async (): Promise<any> => {
    try {
      const response = await onramperClient.get(API_ENDPOINTS.ONRAMPER.DEFAULTS_ALL);
      return response.data?.message || response.data;
    } catch (error) {
      console.error('Failed to fetch defaults from Onramper:', error);
      return null;
    }
  },

  /**
   * Fetches available payment types for a specific fiat/crypto pair in a given country.
   *
   * @param fiat - The fiat currency code (e.g., 'USD').
   * @param country - The country ID/code (e.g., 'IN', 'US').
   * @param crypto - The crypto asset code (e.g., 'USDC').
   * @returns An array of supported payment methods.
   */
  getPaymentTypes: async (
    fiat: string,
    country: string,
    crypto: string,
  ): Promise<PaymentType[]> => {
    try {
      // Use onramperClient to ensure API key is attached
      const response = await onramperClient.get(API_ENDPOINTS.ONRAMPER.PAYMENT_TYPES, {
        params: { source: fiat, destination: crypto, country },
      });

      const paymentTypesObj = response.data?.message || response.data;

      if (
        paymentTypesObj &&
        typeof paymentTypesObj === 'object' &&
        !Array.isArray(paymentTypesObj)
      ) {
        return Object.entries(paymentTypesObj).map(([id, details]: [string, any]) => ({
          paymentTypeId: id,
          ...details,
        })) as PaymentType[];
      }

      if (Array.isArray(paymentTypesObj)) {
        return paymentTypesObj.map((t: any) => ({
          paymentTypeId: t.id || t.paymentTypeId || t.name,
          ...t,
        })) as PaymentType[];
      }

      return [];
    } catch (error) {
      console.error('Failed to fetch payment types from Onramper:', error);
      // throw new Error('Could not retrieve supported payment methods.');
      return [];
    }
  },

  /**
   * Fetches provider quotes for a specific amount, fiat, crypto, and payment method.
   *
   * @param fiat - Fiat currency code (e.g., 'USD').
   * @param crypto - Crypto asset code (e.g., 'USDC').
   * @param amount - The numerical amount of fiat to transfer.
   * @param paymentMethod - The payment method identifier.
   * @param network - The blockchain network (defaults to 'polygon').
   * @returns An array of available provider quotes sorted by payout.
   */
  getQuotes: async (
    sourceCurrency: string,
    destinationCurrency: string,
    amount: number,
    country: string,
    paymentMethod: string,
    network = 'ethereum',
    type = 'buy',
  ): Promise<ProviderQuote[]> => {
    try {
      const response = await onramperClient.get(
        API_ENDPOINTS.ONRAMPER.QUOTES(sourceCurrency, destinationCurrency),
        {
          params: { amount, paymentMethod, network, country, type },
        },
      );

      const data = response.data?.message || response.data || [];
      const validQuotes = Array.isArray(data)
        ? data.map((quote: any) => ({
            ...quote,
            // Normalize after spread so our fields always take precedence
            ramp: quote.ramp || quote.provider || 'unknown',
            payout: Number(quote.payout ?? quote.cryptoAmount ?? 0),
            fees: quote.fees || quote.totalFee || 0,
            totalFee: quote.totalFee || quote.fees || 0,
            networkFee: quote.networkFee || 0,
            rate: quote.rate || 0,
          }))
        : [];

      return validQuotes as ProviderQuote[];
    } catch (error) {
      console.error('Failed to fetch transaction quotes:', error);
      throw new Error('Could not retrieve quotes for the specified transfer.');
    }
  },

  /**
   * Initiates a deposit session on the backend to secure a checkout URL.
   *
   * @param payload - Deposit details including amount, fiat, crypto, and provider.
   * @returns An object containing the secure `paymentUrl` for the widget.
   */
  initiateDeposit: async (payload: {
    onramp: string;
    type: string;
    sourceCurrency: string;
    destinationCurrency: string;
    amount: number;
    country: string;
    paymentMethod: string;
  }): Promise<{
    data: {
      depositId: number;
      widgetUrl: any;
      paymentUrl: string;
    };
  }> => {
    try {
      const response = await axiosClient.post(API_ENDPOINTS.FIAT.DEPOSITS, payload);
      return response.data;
    } catch (error) {
      console.error('Failed to initiate deposit on backend:', error);
      throw new Error('Deposit initiation failed.');
    }
  },

  /**
   * Fetches a deposit record's current status (updated by the OnRamper webhook).
   * Used to poll for completion while the checkout iframe is open.
   */
  getDepositStatus: async (
    depositId: number | string,
  ): Promise<{ id: number; status: string } & Record<string, any>> => {
    const response = await axiosClient.get(`${API_ENDPOINTS.FIAT.DEPOSITS}/${depositId}`);
    return response.data?.data ?? response.data;
  },

  /**
   * Fetches the EIP-712 transaction preparation payload for off-ramp withdrawals.
   *
   * @param withdrawalId - The unique Onramper transaction ID.
   * @param ownerAddress - The address of the Safe proxy owner.
   * @returns The signing payload (TypedData) to be passed to the user's wallet.
   */
  fetchWithdrawalPayload: async (withdrawalId: string, ownerAddress: string): Promise<any> => {
    try {
      const response = await axiosClient.get(API_ENDPOINTS.FIAT.WITHDRAWAL_PREPARE, {
        params: { withdrawalId, ownerAddress },
      });
      // The API returns { success: true, data: { withdrawalId, typedData, meta } }
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ [WalletApi] Fetch withdrawal payload failed:', error);
      throw new Error('Could not prepare withdrawal signature payload. Please try again.');
    }
  },

  /**
   * Fetches EIP-712 payload for wallet-to-wallet withdrawal.
   */
  fetchWalletWithdrawalPayload: async (
    amount: number,
    destinationAddress: string,
    ownerAddress: string,
  ): Promise<any> => {
    try {
      const response = await axiosClient.get(API_ENDPOINTS.FIAT.WALLET_WITHDRAWAL_PREPARE, {
        params: { amount, destinationAddress, ownerAddress },
      });
      return response.data.data || response.data;
    } catch (error) {
      console.error('❌ [WalletApi] Fetch wallet withdrawal payload failed:', error);
      throw new Error('Could not prepare wallet withdrawal payload. Please try again.');
    }
  },

  /**
   * Submits the signed transaction payload to the Polymarket Relayer for execution.
   *
   * @param payload - The original EIP-712 payload.
   * @param signature - The generated cryptographic signature from the user.
   * @returns The resulting transaction hash on the blockchain.
   */
  submitSignedTransaction: async (payload: any, signature: string): Promise<string> => {
    try {
      const relayerResponse = await axios.post(RELAYER_URL, {
        ...payload,
        signature,
      });

      if (!relayerResponse.data?.transactionHash) {
        throw new Error('Relayer did not return a valid transaction hash.');
      }

      return relayerResponse.data.transactionHash;
    } catch (error) {
      console.error('Relayer submission failed:', error);
      throw new Error('Failed to submit signed transaction to network.');
    }
  },

  /**
   * Submits a signed Batch (deposit wallet) bank withdrawal to the backend.
   * Backend holds HMAC credentials and forwards to the Polymarket Relayer.
   * Used only for deposit_wallet users — gnosis_safe users relay client-side.
   *
   * @param withdrawalId - ID returned by prepareBankWithdrawal
   * @param signature - EIP-712 signTypedData(Batch) signature from Privy wallet
   * @param meta - meta object returned from prepareBankWithdrawal
   */
  submitBankWithdrawal: async (
    withdrawalId: string,
    signature: string,
    meta: Record<string, unknown>,
  ): Promise<{ transactionHash?: string; status: string }> => {
    try {
      const response = await axiosClient.post(API_ENDPOINTS.FIAT.WITHDRAWAL_SUBMIT, {
        withdrawalId: Number(withdrawalId),
        signature,
        meta,
      });
      return response.data.data ?? response.data;
    } catch (error) {
      console.error('❌ [WalletApi] Bank withdrawal submit failed:', error);
      throw new Error(
        'Signature submitted but backend relay failed. Your funds are safe — check withdrawal history.',
      );
    }
  },

  /**
   * Confirms the finalized blockchain transaction hash with the backend.
   *
   * @param withdrawalId - The unique Onramper transaction ID.
   * @param transactionHash - The on-chain transaction hash.
   * @param ownerAddress - The address of the Safe proxy owner.
   * @returns The backend confirmation response.
   */
  confirmWithdrawal: async (
    withdrawalId: string,
    transactionHash: string,
    ownerAddress: string,
  ): Promise<any> => {
    try {
      const response = await axiosClient.post(API_ENDPOINTS.FIAT.WITHDRAWAL_CONFIRM, {
        withdrawalId,
        transactionHash,
        ownerAddress,
      });
      return response.data;
    } catch (error) {
      console.error('❌ [WalletApi] Withdrawal confirmation failed:', error);
      throw new Error(
        'Transaction submitted but backend confirmation failed. Your funds are safe.',
      );
    }
  },

  /**
   * Confirms wallet-to-wallet withdrawal after relayer submission.
   */
  confirmWalletWithdrawal: async (withdrawalId: string, transactionHash: string): Promise<any> => {
    try {
      const response = await axiosClient.post(API_ENDPOINTS.FIAT.WALLET_WITHDRAWAL_CONFIRM, {
        withdrawalId: Number(withdrawalId),
        transactionHash,
      });
      return response.data;
    } catch (error) {
      console.error('❌ [WalletApi] Wallet withdrawal confirmation failed:', error);
      throw new Error(
        'Transaction submitted but wallet withdrawal confirmation failed. Your funds are safe.',
      );
    }
  },

  /**
   * Fetches a quote for bridging USDC.e from Polygon to another chain.
   *
   * @param params - Bridge parameters (amount, dest chain, dest token, recipient).
   * @returns Estimated fee and output amount.
   */
  getBridgeQuote: async (
    params: Omit<PrepareBridgeParams, 'ownerAddress'>,
  ): Promise<BridgeQuote> => {
    try {
      const response = await axiosClient.get(API_ENDPOINTS.FIAT.BRIDGE_QUOTE, {
        params,
      });
      return response.data.data as BridgeQuote;
    } catch (error) {
      console.error('Failed to fetch bridge quote:', error);
      throw new Error('Could not retrieve bridge quote.');
    }
  },

  /** Outbound chains/tokens backed by Polymarket Bridge (Ethereum, Solana, Bitcoin, etc.). */
  getBridgeOutboundOptions: async (): Promise<BridgeOutboundRoute[]> => {
    try {
      const response = await axiosClient.get(API_ENDPOINTS.FIAT.BRIDGE_OPTIONS);
      return Array.isArray(response.data.data) ? response.data.data : [];
    } catch (error) {
      console.error('Failed to fetch bridge outbound options:', error);
      throw new Error('Could not load supported bridge networks.');
    }
  },

  /**
   * Prepares a bridge withdrawal by securing a Safe transaction payload from the backend.
   *
   * @param params - Bridge parameters including the user's Safe owner address.
   * @returns The withdrawal ID and the EIP-712 payload for signing.
   */
  prepareBridgeWithdrawal: async (params: PrepareBridgeParams): Promise<PrepareResponse> => {
    try {
      const response = await axiosClient.get(API_ENDPOINTS.FIAT.BRIDGE_PREPARE, {
        params,
      });
      return response.data.data;
    } catch (error) {
      console.error('Failed to prepare bridge withdrawal:', error);
      throw new Error('Bridge preparation failed.');
    }
  },

  /**
   * Submits the user-signed Safe transaction to the backend.
   * The backend holds HMAC builder credentials and forwards to the Polymarket Relayer.
   * This avoids exposing the builder secret to the browser.
   */
  submitBridgeWithdrawal: async (
    withdrawalId: number,
    signature: string,
    meta: PrepareResponse['meta'],
  ): Promise<{ transactionHash?: string; status: string }> => {
    try {
      const response = await axiosClient.post(API_ENDPOINTS.FIAT.BRIDGE_SUBMIT, {
        withdrawalId,
        signature,
        meta,
      });
      return response.data.data ?? response.data;
    } catch (error) {
      console.error('Failed to submit bridge withdrawal:', error);
      throw new Error('Bridge submission failed. Your funds are safe — check withdrawal history.');
    }
  },

  /**
   * Polls the backend for the current status of a bridge withdrawal.
   *
   * @param withdrawalId - The ID returned from the preparation step.
   * @returns The current status (bridging, completed, failed, etc.).
   */
  getBridgeStatus: async (withdrawalId: number): Promise<StatusResponse> => {
    try {
      const response = await axiosClient.get(API_ENDPOINTS.FIAT.BRIDGE_STATUS(withdrawalId));
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch bridge status:', error);
      throw new Error('Could not retrieve bridge status.');
    }
  },

  /**
   * Fetches all asset transfers (deposits/withdrawals) via Alchemy API.
   * Handles pagination automatically to return all results.
   */
  getAssetTransfers: async (apiKey: string, params: any): Promise<any[]> => {
    const allTransfers: any[] = [];
    let pageKey: string | undefined = undefined;

    do {
      const response = await axios.post(API_ENDPOINTS.ALCHEMY.POLYGON_MAINNET(apiKey), {
        jsonrpc: '2.0',
        id: 1,
        method: 'alchemy_getAssetTransfers',
        params: [{ ...params, pageKey }],
      });

      const data = response.data;
      if (data.error) throw new Error(data.error.message || 'Alchemy API error');

      const transfers = data.result?.transfers || [];
      allTransfers.push(...transfers);
      pageKey = data.result?.pageKey;
    } while (pageKey);

    return allTransfers;
  },

  /**
   * Batch fetches top-level transactions by their hashes.
   * Useful for getting the true "Interacted With (To)" address.
   */
  getTransactionsByHashes: async (
    apiKey: string,
    hashes: string[],
  ): Promise<Record<string, any>> => {
    if (hashes.length === 0) return {};

    const uniqueHashes = Array.from(new Set(hashes));
    const chunkSize = 50;
    const results: Record<string, any> = {};

    for (let i = 0; i < uniqueHashes.length; i += chunkSize) {
      const chunk = uniqueHashes.slice(i, i + chunkSize);
      const payload = chunk.map((hash, idx) => ({
        jsonrpc: '2.0',
        id: idx,
        method: 'eth_getTransactionByHash',
        params: [hash],
      }));

      try {
        const response = await axios.post(API_ENDPOINTS.ALCHEMY.POLYGON_MAINNET(apiKey), payload);
        response.data.forEach((d: any) => {
          if (d.result && d.result.hash) {
            results[d.result.hash] = d.result;
          }
        });
      } catch (e) {
        console.error('Batch fetch failed for chunk', e);
      }
    }

    return results;
  },
};
